"use client";

import { c as _c } from "react/compiler-runtime";
import type { BapMasterBackup } from "bitcoin-backup";
import { AppWindow, AtSign, BadgeCheck, Download, KeySquare, type LucideIcon, Menu, ShieldCheck, User, Users, Wallet } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ModeToggle } from "@/components/mode-toggle";
import { SigmaLogo } from "@/components/sigma-logo";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { UserIdentityMenu } from "@/components/user-identity-menu";
import { useIdentity } from "@/contexts/identity-context";
import { useProfileHandle } from "@/hooks/use-profile-handle";
import { useIsOwner, useSession } from "@/hooks/use-session";
import { authClient } from "@/lib/auth-client";
import { downloadEncryptedBackup } from "@/lib/bap/utils";
import { getBrandConfig } from "@/lib/brand-config";
import { clearSensitiveLocalStorage, clearSensitiveSessionStorage } from "@/lib/storage/sensitive-storage";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { cn } from "@/lib/utils";
const SUBPAGES = ["oauth-clients", "credentials", "handle", "wallets", "authorized-apps", "settings", "team"] as const;
interface NavItem {
  label: string;
  description: string;
  href: string;
  icon: LucideIcon;
  /** Path segment that marks this item active; empty string = account root */
  segment: string;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}
function accountSubpageHref(encodedCurrentIdKey: string | null, subpage?: string): string {
  if (!encodedCurrentIdKey) {
    return "/account";
  }
  return subpage ? `/account/${encodedCurrentIdKey}/${subpage}` : `/account/${encodedCurrentIdKey}`;
}
function isItemActive(pathname: string, item: NavItem): boolean {
  if (item.segment === "") {
    return pathname.startsWith("/account") && !SUBPAGES.some(segment => pathname.includes(`/${segment}`));
  }
  return pathname.includes(`/${item.segment}`);
}

/**
 * The account navigation, grouped so the top bar stays quiet:
 * one direct link to the profile, two dropdowns, and Settings living in the
 * identity menu on the right.
 */
export function buildNav(encodedCurrentIdKey: string | null,
// BRC-169 handles are a per-deployment feature. The entry is absent, not
// disabled, when this deployment does not offer them.
handlesEnabled: boolean): {
  account: NavItem;
  groups: NavGroup[];
} {
  const identityItems: NavItem[] = [{
    label: "Wallets",
    description: "Connected wallets and funding",
    href: accountSubpageHref(encodedCurrentIdKey, "wallets"),
    icon: Wallet,
    segment: "wallets"
  }];
  if (handlesEnabled) {
    identityItems.push({
      label: "Handle",
      description: "Your @handle at this ecosystem",
      href: accountSubpageHref(encodedCurrentIdKey, "handle"),
      icon: AtSign,
      segment: "handle"
    });
  }
  identityItems.push({
    label: "Credentials",
    description: "Verifiable credentials bound to this key",
    href: accountSubpageHref(encodedCurrentIdKey, "credentials"),
    icon: BadgeCheck,
    segment: "credentials"
  }, {
    label: "Team",
    description: "Members and invitations",
    href: accountSubpageHref(encodedCurrentIdKey, "team"),
    icon: Users,
    segment: "team"
  });
  return {
    account: {
      label: "Account",
      description: "Profile, keys, and publish status",
      href: accountSubpageHref(encodedCurrentIdKey),
      icon: User,
      segment: ""
    },
    groups: [{
      label: "Identity",
      items: identityItems
    }, {
      label: "Developers",
      items: [{
        label: "OAuth Clients",
        description: "Applications you have registered",
        href: accountSubpageHref(encodedCurrentIdKey, "oauth-clients"),
        icon: KeySquare,
        segment: "oauth-clients"
      }, {
        label: "Authorized Apps",
        description: "Apps you have granted access to",
        href: accountSubpageHref(encodedCurrentIdKey, "authorized-apps"),
        icon: AppWindow,
        segment: "authorized-apps"
      }]
    }]
  };
}
const navLinkClass = "inline-flex h-8 items-center gap-2 rounded-md px-3 font-medium text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[active]:bg-muted data-[active]:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground";
interface DesktopNavProps {
  encodedCurrentIdKey: string | null;
  handlesEnabled: boolean;
  pathname: string;
  isOwner: boolean;
}
function DesktopNav(t0) {
  const $ = _c(24);
  const {
    encodedCurrentIdKey,
    handlesEnabled,
    pathname,
    isOwner
  } = t0;
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== encodedCurrentIdKey || $[1] !== handlesEnabled || $[2] !== pathname) {
    const nav = buildNav(encodedCurrentIdKey, handlesEnabled);
    T1 = NavigationMenu;
    t4 = "hidden md:flex";
    T0 = NavigationMenuList;
    t1 = "space-x-0.5";
    t2 = <NavigationMenuItem><NavigationMenuLink active={isItemActive(pathname, nav.account)} asChild={true} className={navLinkClass}><Link href={nav.account.href}>{nav.account.label}</Link></NavigationMenuLink></NavigationMenuItem>;
    let t5;
    if ($[9] !== pathname) {
      t5 = group => {
        const groupActive = group.items.some(item => isItemActive(pathname, item));
        return <NavigationMenuItem key={group.label}><NavigationMenuTrigger className={cn(navLinkClass, "bg-transparent")} data-active={groupActive ? "" : undefined}>{group.label}</NavigationMenuTrigger><NavigationMenuContent><ul className="grid w-72 gap-0.5 p-1.5">{group.items.map(item_0 => <li key={item_0.href}><NavigationMenuLink active={isItemActive(pathname, item_0)} asChild={true}><Link className="flex items-start gap-3 rounded-md p-2.5 text-sm outline-none transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring data-[active]:bg-muted" href={item_0.href}><item_0.icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /><span className="flex min-w-0 flex-col gap-0.5"><span className="font-medium leading-none">{item_0.label}</span><span className="text-muted-foreground text-xs leading-snug">{item_0.description}</span></span></Link></NavigationMenuLink></li>)}</ul></NavigationMenuContent></NavigationMenuItem>;
      };
      $[9] = pathname;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t3 = nav.groups.map(t5);
    $[0] = encodedCurrentIdKey;
    $[1] = handlesEnabled;
    $[2] = pathname;
    $[3] = T0;
    $[4] = T1;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    T0 = $[3];
    T1 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[11] !== isOwner || $[12] !== pathname) {
    t5 = isOwner && <NavigationMenuItem><NavigationMenuLink active={pathname.startsWith("/admin")} asChild={true} className={navLinkClass}><Link href="/admin/oauth-clients"><ShieldCheck className="h-4 w-4" />Admin</Link></NavigationMenuLink></NavigationMenuItem>;
    $[11] = isOwner;
    $[12] = pathname;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== T0 || $[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== t5) {
    t6 = <T0 className={t1}>{t2}{t3}{t5}</T0>;
    $[14] = T0;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== T1 || $[21] !== t4 || $[22] !== t6) {
    t7 = <T1 className={t4}>{t6}</T1>;
    $[20] = T1;
    $[21] = t4;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
interface MobileNavProps {
  encodedCurrentIdKey: string | null;
  handlesEnabled: boolean;
  pathname: string;
  isOwner: boolean;
  onNavigate: () => void;
}
function MobileNav(t0) {
  const $ = _c(14);
  const {
    encodedCurrentIdKey,
    handlesEnabled,
    pathname,
    isOwner,
    onNavigate
  } = t0;
  let t1;
  let t2;
  if ($[0] !== encodedCurrentIdKey || $[1] !== handlesEnabled || $[2] !== onNavigate || $[3] !== pathname) {
    const nav = buildNav(encodedCurrentIdKey, handlesEnabled);
    const sections = [{
      label: "Account",
      items: [nav.account]
    }, ...nav.groups];
    t1 = "flex flex-col gap-5 p-4";
    t2 = sections.map(section => <div className="flex flex-col gap-1" key={section.label}><span className="px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">{section.label}</span>{section.items.map(item => <Button asChild={true} className="h-10 w-full justify-start gap-3 text-base" key={item.href} variant={isItemActive(pathname, item) ? "secondary" : "ghost"}><Link href={item.href} onClick={onNavigate}><item.icon className="h-4 w-4 text-muted-foreground" />{item.label}</Link></Button>)}</div>);
    $[0] = encodedCurrentIdKey;
    $[1] = handlesEnabled;
    $[2] = onNavigate;
    $[3] = pathname;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isOwner || $[7] !== onNavigate || $[8] !== pathname) {
    t3 = isOwner && <div className="flex flex-col gap-1 border-t pt-4"><Button asChild={true} className="h-10 w-full justify-start gap-3 text-base" variant={pathname.startsWith("/admin") ? "secondary" : "ghost"}><Link href="/admin/oauth-clients" onClick={onNavigate}><ShieldCheck className="h-4 w-4 text-muted-foreground" />Admin</Link></Button></div>;
    $[6] = isOwner;
    $[7] = onNavigate;
    $[8] = pathname;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function Navbar() {
  // Session via TanStack Query - handles caching, revalidation on focus/visibility
  const {
    user
  } = useSession();
  const ownerQuery = useIsOwner(user?.id);
  const isOwner = ownerQuery.data ?? false;

  // Read localStorage only after mount: reading it during render made the
  // server HTML ("Sign Up") disagree with the client ("Sign In") and
  // failed hydration on every page that renders the navbar.
  const [hasEncryptedBackup, setHasEncryptedBackup] = useState(false);
  const [storageChecked, setStorageChecked] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const brandConfig = getBrandConfig();
  const {
    currentIdentity
  } = useIdentity();
  const encodedCurrentIdKey = currentIdentity ? encodeURIComponent(currentIdentity.bapId) : null;
  const profileHandle = useProfileHandle(currentIdentity?.bapId ?? null);
  const handlesEnabled = profileHandle.data?.enabled === true;

  // Sign out flow states
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const [confirmSignOutDialogOpen, setConfirmSignOutDialogOpen] = useState(false);
  const [backupPasswordDialogOpen, setBackupPasswordDialogOpen] = useState(false);
  const [backupPassword, setBackupPassword] = useState("");
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  // Listen for storage changes (e.g., when user signs out and clears backup)
  useEffect(() => {
    const checkBackup = () => {
      setHasEncryptedBackup(!!localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP));
    };
    checkBackup();
    setStorageChecked(true);
    window.addEventListener("storage", checkBackup);
    return () => window.removeEventListener("storage", checkBackup);
  }, []);
  const handleSignOut = () => {
    setSignOutDialogOpen(true);
  };
  const handleEndSession = async () => {
    // Clear decrypted material from session storage only
    clearSensitiveSessionStorage();
    await authClient.signOut();
    window.location.href = "/";
  };
  const handleFullSignOut = async () => {
    // Clear BOTH session and encrypted backup from localStorage
    clearSensitiveSessionStorage();
    clearSensitiveLocalStorage();
    await authClient.signOut();
    window.location.href = "/";
  };
  const handleDownloadBackup = async () => {
    if (!backupPassword.trim()) {
      toast.error("Password is required");
      return;
    }
    try {
      setDownloadingBackup(true);

      // Get backup from sessionStorage
      const backupJson = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
      if (!backupJson) {
        toast.error("No backup found in session");
        return;
      }
      const backup: BapMasterBackup = JSON.parse(backupJson);

      // Download encrypted backup
      await downloadEncryptedBackup(backup, backupPassword);
      toast.success("Master backup downloaded successfully!");

      // Close password dialog and return to confirm sign out dialog
      setBackupPasswordDialogOpen(false);
      setBackupPassword("");
    } catch (error) {
      console.error("Backup download error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to download backup");
    } finally {
      setDownloadingBackup(false);
    }
  };
  return <nav className="sticky top-0 z-50 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
			<div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
				<div className="flex min-w-0 items-center gap-1 md:gap-4">
					{/* Mobile Menu Trigger */}
					{user && currentIdentity && <Sheet onOpenChange={setIsMobileMenuOpen} open={isMobileMenuOpen}>
							<SheetTrigger asChild>
								<Button className="-ml-2 md:hidden" size="icon" variant="ghost">
									<Menu className="h-5 w-5" />
									<span className="sr-only">Toggle menu</span>
								</Button>
							</SheetTrigger>
							<SheetContent className="w-full p-0 sm:max-w-sm" side="left">
								<SheetHeader className="border-b p-5 text-left">
									<SheetTitle className="flex items-center gap-2">
										{brandConfig.logoUrl ? <Image alt={brandConfig.name} className="h-6 w-auto" height={24} src={brandConfig.logoUrl} width={24} /> : <SigmaLogo />}
									</SheetTitle>
								</SheetHeader>
								<MobileNav encodedCurrentIdKey={encodedCurrentIdKey} handlesEnabled={handlesEnabled} isOwner={isOwner} onNavigate={() => setIsMobileMenuOpen(false)} pathname={pathname} />
							</SheetContent>
						</Sheet>}

					<Link aria-label={`${brandConfig.name} home`} className="flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" href="/">
						{brandConfig.logoUrl ? <Image alt={`${brandConfig.name} logo`} className="h-7 w-auto" height={28} src={brandConfig.logoUrl} width={28} /> : <SigmaLogo className="h-5" />}
					</Link>

					{/* Desktop Navigation */}
					{user && currentIdentity && <>
							<span aria-hidden="true" className="hidden h-5 w-px bg-border md:block" />
							<DesktopNav encodedCurrentIdKey={encodedCurrentIdKey} handlesEnabled={handlesEnabled} isOwner={isOwner} pathname={pathname} />
						</>}
				</div>

				<div className="flex shrink-0 items-center gap-2">
					<div className="hidden md:block">
						<ModeToggle />
					</div>

					{user && <UserIdentityMenu onSignOut={handleSignOut} user={user} />}

					{!user && storageChecked && <Button asChild size="sm">
							<Link href={hasEncryptedBackup ? "/login" : "/signup"} prefetch={false}>
								{hasEncryptedBackup ? "Sign In" : "Sign Up"}
							</Link>
						</Button>}
				</div>
			</div>

			{/* Initial Sign Out Dialog - Choose between End Session or Full Sign Out */}
			<AlertDialog onOpenChange={setSignOutDialogOpen} open={signOutDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Sign Out</AlertDialogTitle>
						<AlertDialogDescription>
							Choose how you'd like to sign out
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
						<AlertDialogAction className="w-full" onClick={handleEndSession}>
							End Session
						</AlertDialogAction>
						<Button className="w-full" onClick={() => {
            setSignOutDialogOpen(false);
            setConfirmSignOutDialogOpen(true);
          }} variant="destructive">
							Sign Out (Clear Key)
						</Button>
						<AlertDialogCancel className="w-full">Cancel</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Confirm Sign Out Dialog - Warning with backup option */}
			<AlertDialog onOpenChange={setConfirmSignOutDialogOpen} open={confirmSignOutDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Clear Your Key?</AlertDialogTitle>
						<AlertDialogDescription>
							This will clear your encrypted key from this browser. Make sure
							you've backed it up first.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
						<Button className="flex w-full items-center gap-2" onClick={() => {
            setConfirmSignOutDialogOpen(false);
            setBackupPasswordDialogOpen(true);
          }} variant="outline">
							<Download className="h-4 w-4" />
							Download Master ID
						</Button>
						<AlertDialogAction className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleFullSignOut}>
							Sign Out Anyway
						</AlertDialogAction>
						<AlertDialogCancel className="w-full">Cancel</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Backup Password Dialog */}
			<Dialog onOpenChange={setBackupPasswordDialogOpen} open={backupPasswordDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Encrypt Master Backup</DialogTitle>
						<DialogDescription>
							Enter a password to encrypt your backup file
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label htmlFor="backup-password">Password</Label>
							<Input disabled={downloadingBackup} id="backup-password" onChange={e => setBackupPassword(e.target.value)} onKeyDown={e_0 => {
              if (e_0.key === "Enter" && !downloadingBackup) {
                handleDownloadBackup();
              }
            }} placeholder="Enter encryption password" type="password" value={backupPassword} />
						</div>
					</div>
					<DialogFooter>
						<Button disabled={downloadingBackup} onClick={() => {
            setBackupPasswordDialogOpen(false);
            setBackupPassword("");
            setConfirmSignOutDialogOpen(true);
          }} variant="outline">
							Back
						</Button>
						<Button disabled={downloadingBackup || !backupPassword.trim()} onClick={handleDownloadBackup}>
							{downloadingBackup ? "Downloading..." : "Download Backup"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</nav>;
}